// src/components/UpdateBanner.jsx
// Shows a non-intrusive banner whenever a new SW version is ready to install.
//
// FIXES vs previous version:
//  • updatefound listener is now properly removed on cleanup (was leaking on
//    every remount / StrictMode double-invoke).
//  • statechange listener captured per-install so it can be cleaned up.
//  • Async getRegistration promise is guarded with a `mounted` flag so
//    setState is never called after unmount.
//  • handleDismiss now also clears waitingSW ref.
//  • role="alert" + aria-live added for screen-reader announcement.
//  • Layout: banner uses a fixed width instead of max-content; marginLeft:auto
//    on the button group right-aligns buttons on both one-line and two-line
//    (wrapped) layouts without any JS measurement.

import { useEffect, useState } from 'react';

// Module-level reload guard — survives component remounts.
let _refreshing = false;

export default function UpdateBanner() {
  const [waitingSW, setWaitingSW] = useState(null);
  const [visible,   setVisible]   = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let mounted = true;
    // Holds the removeEventListener call for the updatefound handler so we
    // can run it even though it was registered inside an async callback.
    let removeUpdateFound = () => {};
    // Holds cleanup for the statechange listener on the currently-installing SW.
    let removeStateChange = () => {};

    const setup = (registration) => {
      if (!mounted || !registration) return;

      // A new SW is already waiting (e.g. page loaded while update existed)
      if (registration.waiting) {
        setWaitingSW(registration.waiting);
        setVisible(true);
      }

      // A new SW just finished installing during this session.
      const onUpdateFound = () => {
        const newSW = registration.installing;
        if (!newSW) return;

        // Clean up any previous statechange listener before attaching a new one.
        removeStateChange();

        const onStateChange = () => {
          if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
            if (mounted) {
              setWaitingSW(newSW);
              setVisible(true);
            }
          }
        };
        newSW.addEventListener('statechange', onStateChange);
        removeStateChange = () => newSW.removeEventListener('statechange', onStateChange);
      };

      registration.addEventListener('updatefound', onUpdateFound);
      // Capture the remover so the cleanup function below can call it.
      removeUpdateFound = () => registration.removeEventListener('updatefound', onUpdateFound);
    };

    // Re-use the registration index.js already created — no double-register.
    navigator.serviceWorker.getRegistration('/').then(setup).catch(() => {});

    // Reload when the new SW activates after skipWaiting.
    const onControllerChange = () => {
      if (!_refreshing) {
        _refreshing = true;
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      mounted = false;
      removeUpdateFound();
      removeStateChange();
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const handleUpdate = () => {
    if (!waitingSW) return;
    waitingSW.postMessage({ type: 'SKIP_WAITING' });
    setVisible(false);
  };

  const handleDismiss = () => {
    setVisible(false);
    setWaitingSW(null);
  };

  if (!visible) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position:        'fixed',
        /* Sit above the BottomNav (max 64px) + home indicator + a small gap. */
        bottom:          'calc(72px + env(safe-area-inset-bottom, 0px) + 0.5rem)',
        left:            '50%',
        transform:       'translateX(-50%)',
        zIndex:          9999,
        background:      '#14532d',
        color:           '#fff',
        padding:         '0.65rem 1rem',
        borderRadius:    '0.75rem',
        boxShadow:       '0 4px 16px rgba(0,0,0,0.4)',
        /*
         * Layout strategy
         * ───────────────
         * flex-wrap: wrap lets the row break when the viewport is too narrow.
         * The text span has flex-shrink:0 + white-space:nowrap so it never
         * compresses — it either fits on the same row as the buttons or wraps
         * to its own full-width line (because flex-grow:1 fills remaining space).
         * marginLeft:auto on the button group pushes it to the right on both
         * the single-line layout AND the wrapped second line — no JS needed.
         */
        display:         'flex',
        flexWrap:        'wrap',
        alignItems:      'center',
        gap:             '0.4rem 0.75rem',
        /*
         * Fixed width (not max-content) so the banner doesn't oscillate in
         * size and the flex layout has a stable container to work against.
         * min() gives near-full-width on narrow phones, capped at 32rem on
         * larger screens where one line is always comfortably achievable.
         */
        width:           'min(calc(100vw - 2rem), 32rem)',
        fontSize:        '0.875rem',
        boxSizing:       'border-box',
      }}
    >
      <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
        🎉 A new version is available!
      </span>

      <div
        style={{
          display:     'flex',
          gap:         '0.4rem',
          alignItems:  'center',
          marginLeft:  'auto',   /* right-aligns on both one-line and two-line layouts */
          flexShrink:  0,
        }}
      >
        <button
          onClick={handleUpdate}
          style={{
            background:   '#fff',
            color:        '#14532d',
            border:       'none',
            borderRadius: '0.5rem',
            padding:      '0.35rem 0.75rem',
            fontWeight:   '600',
            cursor:       'pointer',
            whiteSpace:   'nowrap',
            fontSize:     '0.875rem',
          }}
        >
          Update now
        </button>

        <button
          onClick={handleDismiss}
          aria-label="Dismiss update banner"
          style={{
            background: 'transparent',
            color:      '#fff',
            border:     'none',
            cursor:     'pointer',
            fontSize:   '1rem',
            lineHeight: 1,
            padding:    '0 0.15rem',
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}